/**
 * Subtasks API services
 *
 * Handles both template subtasks (checklist items on templates)
 * and instance subtasks (actual checklist items on task instances).
 */

import apiClient from './client';

// ============================================================================
// Types
// ============================================================================

export interface TemplateSubtask {
  id: string;
  templateId: string;
  title: string;
  description: string | null;
  sortOrder: number;
  isRequired: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InstanceSubtask {
  id: string;
  instanceId: string;
  templateSubtaskId: string | null;
  title: string;
  description: string | null;
  sortOrder: number;
  isCompleted: boolean;
  completedAt: string | null;
  completedBy: {
    id: string;
    name: string;
  } | null;
  isRequired: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SubtaskProgress {
  total: number;
  completed: number;
  required: number;
  incompleteRequired: number;
}

export interface CreateTemplateSubtask {
  title: string;
  description?: string;
  sortOrder?: number;
  isRequired?: boolean;
}

export interface UpdateTemplateSubtask {
  title?: string;
  description?: string | null;
  sortOrder?: number;
  isRequired?: boolean;
}

export interface CreateInstanceSubtask {
  title: string;
  description?: string;
  sortOrder?: number;
  isRequired?: boolean;
}

export interface UpdateInstanceSubtask {
  title?: string;
  description?: string | null;
  sortOrder?: number;
  isRequired?: boolean;
  isCompleted?: boolean;
}

// ============================================================================
// Transform functions
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformTemplateSubtask = (data: Record<string, any>): TemplateSubtask => ({
  id: data.id,
  templateId: data.templateId || data.template_id,
  title: data.title,
  description: data.description,
  sortOrder: data.sortOrder ?? data.sort_order ?? 0,
  isRequired: data.isRequired ?? data.is_required ?? false,
  createdAt: data.createdAt || data.created_at,
  updatedAt: data.updatedAt || data.updated_at,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformInstanceSubtask = (data: Record<string, any>): InstanceSubtask => ({
  id: data.id,
  instanceId: data.instanceId || data.instance_id,
  templateSubtaskId: data.templateSubtaskId || data.template_subtask_id,
  title: data.title,
  description: data.description,
  sortOrder: data.sortOrder ?? data.sort_order ?? 0,
  isCompleted: data.isCompleted ?? data.is_completed ?? false,
  completedAt: data.completedAt || data.completed_at,
  completedBy: data.completedBy || data.completed_by,
  isRequired: data.isRequired ?? data.is_required ?? false,
  createdAt: data.createdAt || data.created_at,
  updatedAt: data.updatedAt || data.updated_at,
});

// ============================================================================
// Template Subtasks API
// ============================================================================

export const templateSubtasksApi = {
  /**
   * List subtasks for a template
   */
  list: async (templateId: string): Promise<TemplateSubtask[]> => {
    const response = await apiClient.get(`/v1/subtasks/templates/${templateId}`);
    const items = response.data.items || [];
    return items.map(transformTemplateSubtask);
  },

  /**
   * Create a subtask on a template
   */
  create: async (templateId: string, data: CreateTemplateSubtask): Promise<TemplateSubtask> => {
    const payload = {
      title: data.title,
      description: data.description,
      sort_order: data.sortOrder ?? 0,
      is_required: data.isRequired ?? false,
    };
    const response = await apiClient.post(`/v1/subtasks/templates/${templateId}`, payload);
    return transformTemplateSubtask(response.data);
  },

  /**
   * Update a template subtask
   */
  update: async (templateId: string, subtaskId: string, data: UpdateTemplateSubtask): Promise<TemplateSubtask> => {
    const payload: Record<string, unknown> = {};
    if (data.title !== undefined) payload.title = data.title;
    if (data.description !== undefined) payload.description = data.description;
    if (data.sortOrder !== undefined) payload.sort_order = data.sortOrder;
    if (data.isRequired !== undefined) payload.is_required = data.isRequired;

    const response = await apiClient.put(`/v1/subtasks/templates/${templateId}/${subtaskId}`, payload);
    return transformTemplateSubtask(response.data);
  },

  /**
   * Delete a template subtask
   */
  delete: async (templateId: string, subtaskId: string): Promise<void> => {
    await apiClient.delete(`/v1/subtasks/templates/${templateId}/${subtaskId}`);
  },

  /**
   * Reorder template subtasks
   */
  reorder: async (templateId: string, subtaskIds: string[]): Promise<void> => {
    await apiClient.post(`/v1/subtasks/templates/${templateId}/reorder`, {
      subtask_ids: subtaskIds,
    });
  },
};

// ============================================================================
// Instance Subtasks API
// ============================================================================

export const instanceSubtasksApi = {
  /**
   * List subtasks for an instance
   */
  list: async (instanceId: string, includeCompleted = true): Promise<{ items: InstanceSubtask[]; progress: SubtaskProgress }> => {
    const response = await apiClient.get(`/v1/subtasks/instances/${instanceId}`, {
      params: { include_completed: includeCompleted },
    });
    return {
      items: (response.data.items || []).map(transformInstanceSubtask),
      progress: response.data.progress || { total: 0, completed: 0, required: 0, incompleteRequired: 0 },
    };
  },

  /**
   * Create a subtask on an instance
   */
  create: async (instanceId: string, data: CreateInstanceSubtask): Promise<InstanceSubtask> => {
    const payload = {
      title: data.title,
      description: data.description,
      sort_order: data.sortOrder ?? 0,
      is_required: data.isRequired ?? false,
    };
    const response = await apiClient.post(`/v1/subtasks/instances/${instanceId}`, payload);
    return transformInstanceSubtask(response.data);
  },

  /**
   * Update an instance subtask
   */
  update: async (instanceId: string, subtaskId: string, data: UpdateInstanceSubtask): Promise<InstanceSubtask> => {
    const payload: Record<string, unknown> = {};
    if (data.title !== undefined) payload.title = data.title;
    if (data.description !== undefined) payload.description = data.description;
    if (data.sortOrder !== undefined) payload.sort_order = data.sortOrder;
    if (data.isRequired !== undefined) payload.is_required = data.isRequired;
    if (data.isCompleted !== undefined) payload.is_completed = data.isCompleted;

    const response = await apiClient.put(`/v1/subtasks/instances/${instanceId}/${subtaskId}`, payload);
    return transformInstanceSubtask(response.data);
  },

  /**
   * Toggle subtask completion
   */
  toggle: async (instanceId: string, subtaskId: string): Promise<InstanceSubtask> => {
    const response = await apiClient.post(`/v1/subtasks/instances/${instanceId}/${subtaskId}/toggle`);
    return transformInstanceSubtask(response.data);
  },

  /**
   * Delete an instance subtask
   */
  delete: async (instanceId: string, subtaskId: string): Promise<void> => {
    await apiClient.delete(`/v1/subtasks/instances/${instanceId}/${subtaskId}`);
  },

  /**
   * Reorder instance subtasks
   */
  reorder: async (instanceId: string, subtaskIds: string[]): Promise<void> => {
    await apiClient.post(`/v1/subtasks/instances/${instanceId}/reorder`, {
      subtask_ids: subtaskIds,
    });
  },

  /**
   * Copy subtasks from template to instance
   */
  copyFromTemplate: async (instanceId: string): Promise<{ copied: number }> => {
    const response = await apiClient.post(`/v1/subtasks/instances/${instanceId}/copy-from-template`);
    return { copied: response.data.copied || 0 };
  },
};
